import apiFetch from "@wordpress/api-fetch";
import { useDispatch, useSelect } from "@wordpress/data";
import { __ } from "@wordpress/i18n";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import Button from "../../button";
import { transition } from "../auth-state-machine";
import { authStore } from "../auth-store";
import { handleApiErrorOrReload, validateEmail } from "../auth-utils";
import { defaultSubmitTextForStage } from "../default-copy";

type Props = { copySubmitOverride?: string };

export const EmailEntryStage: React.FC<Props> = ({ copySubmitOverride }) => {
	const email = useSelect((select) => select(authStore).getEmail(), []);
	const intent = useSelect((select) => select(authStore).getIntent(), []);
	const error = useSelect((select) => select(authStore).getError(), []);
	const isSubmitting = useSelect(
		(select) => select(authStore).isSubmitting(),
		[],
	);
	const canFlipToSignup = useSelect(
		(select) => select(authStore).getCanFlipToSignup(),
		[],
	);

	const { setEmail, setError, setSubmitting, setStage, setCanFlipToSignup } =
		useDispatch(authStore);

	const [localError, setLocalError] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	useEffect(() => () => abortRef.current?.abort(), []);

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const validationErr = validateEmail(email);
		if (validationErr) {
			setLocalError(validationErr);
			return;
		}
		setLocalError(null);
		setError(null);
		setSubmitting(true);
		abortRef.current?.abort();
		const ac = new AbortController();
		abortRef.current = ac;
		try {
			const res = await apiFetch<{ exists: boolean }>({
				path: "/leat/v1/auth/check-email",
				method: "POST",
				data: { email },
				signal: ac.signal,
			});
			const next = transition(
				{ stage: "emailEntry", email },
				{ type: "emailLookupResult", exists: res.exists },
				intent,
			);
			setCanFlipToSignup(Boolean(next.canFlipToSignup));
			if (next.error) setError(next.error);
			else setError(null);
			setStage(next.stage);
		} catch (err) {
			if ((err as { name?: string })?.name === "AbortError") return;
			const msg = handleApiErrorOrReload(err);
			if (msg) setError(msg);
		} finally {
			if (!ac.signal.aborted) setSubmitting(false);
		}
	};

	return (
		<form onSubmit={onSubmit} noValidate>
			<div className="leat-auth-modal__field">
				<label htmlFor="leat-auth-email">{__("Email", "leat-crm")}</label>
				<input
					id="leat-auth-email"
					type="email"
					autoComplete="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					aria-describedby={localError ? "leat-auth-email-error" : undefined}
				/>
				{localError && (
					<span id="leat-auth-email-error" role="alert">
						{localError}
					</span>
				)}
			</div>
			{error && (
				<div className="leat-auth-modal__submit-error" role="alert">
					{error}
				</div>
			)}
			{canFlipToSignup && (
				<button
					type="button"
					className="leat-auth-modal__flip-link"
					onClick={() => {
						setCanFlipToSignup(false);
						setError(null);
						setStage("createAccount");
					}}
				>
					{__("Create one instead", "leat-crm")}
				</button>
			)}
			<Button
				type="submit"
				variant="contained"
				className="leat-auth-modal__submit leat-button--pill"
				disabled={isSubmitting}
			>
				{isSubmitting
					? __("Checking…", "leat-crm")
					: (copySubmitOverride ?? defaultSubmitTextForStage("emailEntry"))}
			</Button>
		</form>
	);
};
